/**
 * Phase 20 — Consolidated Exception Center Widget
 * Centralizes cross-lifecycle operational discrepancies (SLA breaches, delivery delays,
 * invoice price/qty variances, payment clearing flags, and SAP sync timeouts) with deduplication.
 * Uses h-full flex layout to eliminate empty gaps and align with SupplierDeliveryHealthWidget.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardExceptionItem, ActionSeverity } from '@/types/domain/procurement-dashboard';
import {
  AlertTriangle,
  AlertOctagon,
  Info,
  ArrowRight,
  CheckCircle2,
  ShieldAlert,
} from 'lucide-react';

interface ExceptionCenterWidgetProps {
  exceptions: DashboardExceptionItem[];
}

export const ExceptionCenterWidget: React.FC<ExceptionCenterWidgetProps> = ({ exceptions }) => {
  const navigate = useNavigate();

  const getSeverityBadge = (sev: ActionSeverity) => {
    switch (sev) {
      case 'CRITICAL':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30">
            <AlertOctagon className="h-3 w-3 shrink-0" />
            Critical Discrepancy
          </span>
        );
      case 'HIGH':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            High Exception
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
            <Info className="h-3 w-3 shrink-0" />
            Notice
          </span>
        );
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-2xs font-heading space-y-4 h-full flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-foreground tracking-tight">
                Procurement Exception Center
              </h2>
              <p className="text-xs text-foreground-secondary font-medium">
                Consolidated active discrepancies requiring cross-departmental resolution
              </p>
            </div>
          </div>

          <span className="px-2.5 py-1 rounded-full text-xs font-black bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
            {exceptions.length} Active Exceptions
          </span>
        </div>
      </div>

      {exceptions.length === 0 ? (
        <div className="py-6 text-center space-y-2 flex-1 flex flex-col items-center justify-center">
          <div className="mx-auto w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <p className="text-sm font-extrabold text-foreground">Zero Active Exceptions</p>
          <p className="text-xs text-foreground-secondary max-w-sm mx-auto">
            All three-way financial matches, delivery receipts, and automated payment runs are cleared cleanly.
          </p>
        </div>
      ) : (
        <div className="space-y-3 flex-1 flex flex-col justify-between">
          {exceptions.slice(0, 3).map((exc) => (
            <div
              key={exc.id}
              onClick={() => navigate(exc.targetRoute)}
              className="p-3.5 rounded-xl border border-border/80 bg-surface hover:border-primary/50 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer group"
            >
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {getSeverityBadge(exc.severity)}
                  <span className="text-[10px] font-mono font-black uppercase text-foreground-muted px-1.5 py-0.5 rounded bg-muted/60">
                    {exc.referenceNumber}
                  </span>
                  <span className="text-[10px] font-semibold text-foreground-muted">
                    Logged: {exc.occurredAt}
                  </span>
                </div>
                <h3 className="text-sm font-extrabold text-foreground group-hover:text-primary transition-colors">
                  {exc.title}
                </h3>
                <p className="text-xs text-foreground-secondary leading-relaxed">{exc.description}</p>
              </div>

              <div className="shrink-0 pt-2 sm:pt-0">
                <span className="inline-flex items-center text-xs font-extrabold text-primary group-hover:underline">
                  Resolve Exception <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom Reassurance Footer (Matching Height with Supplier Delivery Health) */}
      <div className="p-3 rounded-xl bg-muted/40 border border-border/60 flex items-center justify-between text-xs mt-auto">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-500" />
          <span className="text-foreground-secondary font-medium">
            Automated Exception Resolution: <strong className="text-foreground font-bold">Policy Governed</strong>
          </span>
        </div>
        <span className="text-xs font-bold text-primary hover:underline cursor-pointer">
          Audit All Flags →
        </span>
      </div>
    </div>
  );
};
